import type { User, Permission } from '../types';
import { isAdmin as checkIsAdmin, getUserRoleSlugs } from '../utils/permissions';

/**
 * Advanced Policy Engine for RBAC
 * Provides a declarative, policy-as-code approach to access control
 */

export interface PolicyRule {
  id: string;
  name: string;
  description: string;
  conditions: PolicyCondition[];
  effect: 'allow' | 'deny';
  priority: number;
}

export interface PolicyCondition {
  type: 'role' | 'permission' | 'attribute' | 'context' | 'time' | 'custom';
  operator: 'equals' | 'in' | 'not_in' | 'contains' | 'greater_than' | 'less_than' | 'exists' | 'custom';
  field: string;
  value: any;
  customFunction?: (user: User, context: any) => boolean;
}

export interface PolicyContext {
  resource?: string;
  action?: string;
  scope?: string;
  resourceId?: string;
  podId?: string;
  projectId?: string;
  clientId?: string;
  timestamp?: Date;
  ipAddress?: string;
  userAgent?: string;
  [key: string]: any;
}

export interface PolicyEvaluationResult {
  allowed: boolean;
  reason: string;
  appliedRules: string[];
  deniedBy?: string;
  allowedBy?: string;
}

/**
 * Advanced Policy Engine Class
 */
export class AdvancedPolicyEngine {
  private static policies: PolicyRule[] = [];

  /**
   * Register a policy rule
   */
  static registerPolicy(policy: PolicyRule): void {
    // Remove existing policy with same ID
    this.policies = this.policies.filter(p => p.id !== policy.id);
    
    // Add new policy and sort by priority (higher priority first)
    this.policies.push(policy);
    this.policies.sort((a, b) => b.priority - a.priority);
  }

  /**
   * Register multiple policies
   */
  static registerPolicies(policies: PolicyRule[]): void {
    policies.forEach(policy => this.registerPolicy(policy));
  }

  /**
   * Evaluate access based on policies
   */
  static evaluateAccess(
    user: User,
    permissions: Permission[],
    context: PolicyContext
  ): PolicyEvaluationResult {
    const appliedRules: string[] = [];
    let finalDecision = false;
    let reason = 'No applicable policies found';
    let deniedBy: string | undefined;
    let allowedBy: string | undefined;

    // Admin bypass (checks roles[] superPermission first, then legacy role field)
    if (checkIsAdmin(user)) {
      return {
        allowed: true,
        reason: 'Admin user has unrestricted access',
        appliedRules: ['admin-bypass'],
        allowedBy: 'admin-bypass'
      };
    }

    // Evaluate policies in priority order
    for (const policy of this.policies) {
      if (this.evaluateConditions(policy.conditions, user, permissions, context)) {
        appliedRules.push(policy.id);
        
        if (policy.effect === 'deny') {
          return {
            allowed: false,
            reason: `Access denied by policy: ${policy.name}`,
            appliedRules,
            deniedBy: policy.id
          };
        } else if (policy.effect === 'allow') {
          finalDecision = true;
          reason = `Access allowed by policy: ${policy.name}`;
          allowedBy = policy.id;
          // Continue to check for deny policies with higher priority
        }
      }
    }

    return {
      allowed: finalDecision,
      reason,
      appliedRules,
      deniedBy,
      allowedBy
    };
  }

  /**
   * Evaluate policy conditions
   */
  private static evaluateConditions(
    conditions: PolicyCondition[],
    user: User,
    permissions: Permission[],
    context: PolicyContext
  ): boolean {
    return conditions.every(condition => 
      this.evaluateCondition(condition, user, permissions, context)
    );
  }

  /**
   * Evaluate a single condition
   */
  private static evaluateCondition(
    condition: PolicyCondition,
    user: User,
    permissions: Permission[],
    context: PolicyContext
  ): boolean {
    switch (condition.type) {
      case 'role':
        return this.evaluateRoleCondition(condition, user);
      
      case 'permission':
        return this.evaluatePermissionCondition(condition, permissions, context);
      
      case 'attribute':
        return this.evaluateAttributeCondition(condition, user);
      
      case 'context':
        return this.evaluateContextCondition(condition, context);
      
      case 'time':
        return this.evaluateTimeCondition(condition);
      
      case 'custom':
        return condition.customFunction ? condition.customFunction(user, context) : false;
      
      default:
        return false;
    }
  }

  /**
   * Evaluate role-based conditions
   */
  private static evaluateRoleCondition(condition: PolicyCondition, user: User): boolean {
    // RBAC v2: Get all role slugs from roles[], fallback to legacy role field
    const userSlugs: string[] = getUserRoleSlugs(user);

    switch (condition.operator) {
      case 'equals':
        return userSlugs.includes(condition.value as string);

      case 'in':
        return Array.isArray(condition.value) && userSlugs.some(s => condition.value.includes(s));

      case 'not_in':
        return Array.isArray(condition.value) && !userSlugs.some(s => condition.value.includes(s));
      
      default:
        return false;
    }
  }

  /**
   * Evaluate permission-based conditions
   */
  private static evaluatePermissionCondition(
    condition: PolicyCondition,
    permissions: Permission[],
    context: PolicyContext
  ): boolean {
    const { resource, action, scope } = condition.value || {};

    // If condition values are null, use context values (for dynamic permission checking)
    const targetResource = resource || context.resource;
    const targetAction = action || context.action;
    const targetScope = scope || context.scope;

    return permissions.some(permission => {
      const resourceMatch = !targetResource || permission.resource === targetResource;
      const actionMatch = !targetAction || permission.action === targetAction;
      const scopeMatch = !targetScope || permission.scope === targetScope || permission.scope === 'global';

      return resourceMatch && actionMatch && scopeMatch;
    });
  }

  /**
   * Evaluate attribute-based conditions
   */
  private static evaluateAttributeCondition(condition: PolicyCondition, user: User): boolean {
    const userValue = this.getNestedValue(user, condition.field);
    
    switch (condition.operator) {
      case 'equals':
        return userValue === condition.value;
      
      case 'in':
        return Array.isArray(condition.value) && condition.value.includes(userValue);
      
      case 'contains':
        return Array.isArray(userValue) && userValue.includes(condition.value);
      
      case 'exists':
        return userValue !== undefined && userValue !== null;
      
      default:
        return false;
    }
  }

  /**
   * Evaluate context-based conditions
   */
  private static evaluateContextCondition(condition: PolicyCondition, context: PolicyContext): boolean {
    const contextValue = this.getNestedValue(context, condition.field);
    
    switch (condition.operator) {
      case 'equals':
        return contextValue === condition.value;
      
      case 'in':
        return Array.isArray(condition.value) && condition.value.includes(contextValue);
      
      case 'exists':
        return contextValue !== undefined && contextValue !== null;
      
      default:
        return false;
    }
  }

  /**
   * Evaluate time-based conditions
   */
  private static evaluateTimeCondition(condition: PolicyCondition): boolean {
    const now = new Date();
    const conditionTime = new Date(condition.value);
    
    switch (condition.operator) {
      case 'greater_than':
        return now > conditionTime;
      
      case 'less_than':
        return now < conditionTime;
      
      default:
        return false;
    }
  }

  /**
   * Get nested object value by path
   */
  private static getNestedValue(obj: any, path: string): any {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  }

  /**
   * Get all registered policies
   */
  static getPolicies(): PolicyRule[] {
    return [...this.policies];
  }

  /**
   * Remove a policy
   */
  static removePolicy(policyId: string): void {
    this.policies = this.policies.filter(p => p.id !== policyId);
  }

  /**
   * Clear all policies
   */
  static clearPolicies(): void {
    this.policies = [];
  }

  /**
   * Validate policy structure
   */
  static validatePolicy(policy: PolicyRule): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!policy.id) errors.push('Policy ID is required');
    if (!policy.name) errors.push('Policy name is required');
    if (!['allow', 'deny'].includes(policy.effect)) errors.push('Policy effect must be "allow" or "deny"');
    if (typeof policy.priority !== 'number') errors.push('Policy priority must be a number');
    if (!Array.isArray(policy.conditions)) errors.push('Policy conditions must be an array');

    // Validate conditions
    policy.conditions?.forEach((condition, index) => {
      if (!condition.type) errors.push(`Condition ${index}: type is required`);
      if (!condition.operator) errors.push(`Condition ${index}: operator is required`);
      if (!condition.field) errors.push(`Condition ${index}: field is required`);
    });

    return {
      valid: errors.length === 0,
      errors
    };
  }
}

/** Monday–Friday, 9:00–18:00 in the browser's local time. */
export const isWithinBusinessHours = (date: Date): boolean => {
  const day = date.getDay();
  const hour = date.getHours();
  return day >= 1 && day <= 5 && hour >= 9 && hour < 18;
};

/**
 * Predefined Policy Sets
 */
export const PolicySets = {
  /**
   * Basic RBAC policies based on roles
   */
  BASIC_RBAC: [
    {
      id: 'permission-based-access',
      name: 'Permission-Based Access',
      description: 'Allow access if user has the required permission',
      conditions: [
        {
          type: 'permission' as const,
          operator: 'exists' as const,
          field: 'permission',
          value: {
            resource: null, // Will be dynamically set from context
            action: null,   // Will be dynamically set from context
            scope: null     // Will be dynamically set from context
          }
        }
      ],
      effect: 'allow' as const,
      priority: 500
    },
    {
      id: 'admin-full-access',
      name: 'Admin Full Access',
      description: 'Administrators have full access to all resources',
      conditions: [
        {
          type: 'role' as const,
          operator: 'equals' as const,
          field: 'role',
          value: 'admin'
        }
      ],
      effect: 'allow' as const,
      priority: 1000
    },
    {
      id: 'operations-management',
      name: 'Operations Management Access',
      description: 'Operations team can manage pods and projects globally',
      conditions: [
        {
          type: 'role' as const,
          operator: 'equals' as const,
          field: 'role',
          value: 'operations'
        },
        {
          type: 'context' as const,
          operator: 'in' as const,
          field: 'resource',
          value: ['pods', 'projects', 'users']
        }
      ],
      effect: 'allow' as const,
      priority: 900
    },
    {
      id: 'pod-lead-scope',
      name: 'Pod Lead Scoped Access',
      description: 'Pod leads can manage their assigned pods',
      conditions: [
        {
          type: 'role' as const,
          operator: 'equals' as const,
          field: 'role',
          value: 'pod_lead'
        },
        {
          type: 'context' as const,
          operator: 'equals' as const,
          field: 'scope',
          value: 'pod'
        }
      ],
      effect: 'allow' as const,
      priority: 800
    }
  ],

  /**
   * Time-based access policies
   */
  TIME_BASED: [
    {
      id: 'business-hours-only',
      name: 'Business Hours Access',
      description: 'Restrict access to business hours for certain operations',
      conditions: [
        {
          type: 'custom' as const,
          operator: 'custom' as const,
          field: 'time',
          value: null,
          // The policy DENIES when all conditions match, so this must match OUTSIDE business hours.
          customFunction: () => !isWithinBusinessHours(new Date())
        },
        {
          type: 'context' as const,
          operator: 'in' as const,
          field: 'action',
          value: ['delete', 'configure']
        }
      ],
      effect: 'deny' as const,
      priority: 950
    }
  ],

  /**
   * Security policies
   */
  SECURITY: [
    {
      id: 'prevent-self-role-change',
      name: 'Prevent Self Role Change',
      description: 'Users cannot change their own role',
      conditions: [
        {
          type: 'context' as const,
          operator: 'equals' as const,
          field: 'resource',
          value: 'users'
        },
        {
          type: 'context' as const,
          operator: 'equals' as const,
          field: 'action',
          value: 'edit'
        },
        {
          type: 'custom' as const,
          operator: 'custom' as const,
          field: 'self_edit',
          value: null,
          customFunction: (user: User, context: any) => {
            return context.resourceId === user.id && context.field === 'role';
          }
        }
      ],
      effect: 'deny' as const,
      priority: 999
    }
  ]
};

/**
 * Policy Configuration Manager
 */
export class PolicyConfigManager {
  /**
   * Load default policies.
   *
   * `timeBased` mirrors `RBACConfig.features.timeBasedAccess` (on in
   * staging/production): non-admin `configure` / `delete` checks are denied
   * outside business hours. Frontend-only — the backend enforces no time
   * rules (docs/system-admin-permissions.md).
   */
  static loadDefaultPolicies({ timeBased = true }: { timeBased?: boolean } = {}): void {
    const allPolicies = [
      ...PolicySets.BASIC_RBAC,
      ...(timeBased ? PolicySets.TIME_BASED : []),
      ...PolicySets.SECURITY
    ];

    AdvancedPolicyEngine.registerPolicies(allPolicies);
  }

  /**
   * Load policies from configuration
   */
  static loadPoliciesFromConfig(config: any): void {
    if (config.policies && Array.isArray(config.policies)) {
      config.policies.forEach((policy: PolicyRule) => {
        const validation = AdvancedPolicyEngine.validatePolicy(policy);
        if (validation.valid) {
          AdvancedPolicyEngine.registerPolicy(policy);
        } else {
          console.warn(`Invalid policy ${policy.id}:`, validation.errors);
        }
      });
    }
  }

  /**
   * Export current policies to configuration
   */
  static exportPolicies(): any {
    return {
      policies: AdvancedPolicyEngine.getPolicies(),
      exportedAt: new Date().toISOString(),
      version: '1.0'
    };
  }
}

export default AdvancedPolicyEngine;
